import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import { NARROW_WIDTH } from './layoutStyles';
import { useContainerWidth } from './useContainerWidth';
import { useFillHeight } from './useFillHeight';

export interface PageFrameProps {
  /** `<PageHeader />`. Locked. */
  header: ReactNode;
  /** `<Tabs />`, optional. Locked. */
  tabs?: ReactNode;
  /** Fixed rows above the data region (filters, a summary sentence). */
  toolbar?: ReactNode;
  /**
   * The data region. A layout decides what scrolls inside it. A function receives `narrow`: true
   * once the frame's own width is at or below the narrow threshold.
   */
  children: ReactNode | ((frame: { narrow: boolean }) => ReactNode);
  /** A fixed row at the foot, e.g. `<SaveBar />`. */
  footer?: ReactNode;
  /** `viewport`: measure what is left in the window. `parent`: fill a parent of definite height. */
  fill?: 'viewport' | 'parent';
}

/**
 * The page frame every layout stands on (CLAUDE.md "A screen header never scrolls"): locked
 * header, tabs, toolbar and footer; one data region that is `flex: 1; min-height: 0`; the
 * page itself cannot scroll. Layouts supply the region's contents and say what scrolls in it.
 *
 * The frame is a size container, so a layout answers to its own width (a pane, a dialog, a
 * phone) and not to the window's.
 */
export function PageFrame({
  header,
  tabs,
  toolbar,
  children,
  footer,
  fill = 'viewport',
}: PageFrameProps) {
  const [heightRef, height] = useFillHeight<HTMLDivElement>();
  const [widthRef, width] = useContainerWidth<HTMLDivElement>();
  const narrow = width !== undefined && width <= NARROW_WIDTH;
  const ref = (node: HTMLDivElement | null) => {
    widthRef(node);
    if (fill === 'viewport') heightRef(node);
  };
  const sized =
    fill === 'viewport'
      ? height !== undefined
        ? { height }
        : { minHeight: 480 }
      : { height: '100%' };

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
        containerType: 'inline-size',
        ...sized,
      }}
    >
      {header}
      {tabs ? <Box sx={{ flex: 'none', mt: 3 }}>{tabs}</Box> : null}
      {toolbar ? <Box sx={{ flex: 'none' }}>{toolbar}</Box> : null}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', mt: 3 }}>
        {typeof children === 'function' ? children({ narrow }) : children}
      </Box>
      {footer ? <Box sx={{ flex: 'none' }}>{footer}</Box> : null}
    </Box>
  );
}

export { NARROW_WIDTH };
